/* eslint-disable max-len */
// =============================================================================
// XUẤT "Bản đăng ký tham gia Giải thưởng KHCN Quả Cầu Vàng" (.docx) theo MẪU 2026.
// Cách làm: nạp template tối giản QCV2026_Ho_so.docx (chỉ có 1 placeholder
// {~~content}), dựng TOÀN BỘ nội dung hồ sơ thành 1 chuỗi HTML rồi inject qua
// docxtemplater + html-module. Mọi JSON.parse đều được bọc try/catch, dữ liệu
// thiếu/legacy không làm vỡ file.
// =============================================================================
import dayjs from "dayjs";
import PizZip from "pizzip";
import PizZipUtils from "pizzip/utils/index.js";
import Docxtemplater from "docxtemplater";

import { saveAs } from "file-saver";

import sample from "../assets/docs/QCV2026_Ho_so.docx";
import HTMLModule from "./docxtemplater-html-module";
import { resolveQcvLabel } from "./qcvScoreOptions";

import {
  FORM_CONSTANTS,
  REPORTS_CONSTANTS,
  RESEARCH_CONSTANTS,
  SOLUTIONS_CONSTANTS,
  MONOGRAPHS_CONSTANTS,
  TECH_COMPANY_CONSTANTS,
  RESEARCHPROJECTS_CONSTANTS,
} from "../constants";
import { CATEGORY } from "../constants/enums";
import {
  QCV2026_REWARD_LEVEL_OPTIONS,
  QCV2026_TYPICAL_WORK_FIELDS,
} from "../constants/qcv2026.config";

const loadFile = (url, callback) => PizZipUtils.getBinaryContent(url, callback);

// --- Helpers an toàn -------------------------------------------------------
const parseJson = (value, fallback = []) => {
  try {
    const parsed = JSON.parse(value || "null");
    return parsed == null ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const esc = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const stripTags = (value) =>
  String(value == null ? "" : value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const findTitle = (options, value) =>
  (options || []).find((o) => o.value === value)?.title || "";

const fmtDate = (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "");

const checkbox = (selected) => (selected ? "☒" : "☐");

// Ô "minh chứng": link ngoài (DOI...) dùng nguyên, file upload thì ghép domain.
const previewCell = (domain, value) => {
  if (!value) return "";
  const href = `${domain || ""}${value}`;
  return `<a href="${esc(href)}">Minh chứng</a>`;
};

// --- Style bảng ------------------------------------------------------------
const TABLE_STYLE =
  "width:100%;border-collapse:collapse;margin:4px 0 14px;font-size:13px;";
const TH =
  "border:1px solid #000;padding:4px 6px;background:#d9d9d9;text-align:center;font-weight:bold;";
const TD = "border:1px solid #000;padding:4px 6px;vertical-align:top;";

// rows: mảng các mảng cell (đã là HTML, KHÔNG tự escape thêm ở đây).
const buildTable = (headers, rows) => {
  const head = headers.map((h) => `<th style="${TH}">${h}</th>`).join("");
  const body =
    rows && rows.length
      ? rows
          .map(
            (r) =>
              `<tr>${r
                .map((c) => `<td style="${TD}">${c}</td>`)
                .join("")}</tr>`
          )
          .join("")
      : `<tr><td style="${TD}text-align:center;font-style:italic;" colspan="${headers.length}">Không có</td></tr>`;
  return `<table style="${TABLE_STYLE}"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
};

const sectionTitle = (text) =>
  `<p style="margin:16px 0 4px;font-size:15px;font-weight:bold;">${esc(text)}</p>`;

const subTitle = (text) =>
  `<p style="margin:10px 0 2px;font-weight:bold;">${esc(text)}</p>`;

const line = (label, value) =>
  `<p style="margin:3px 0;"><b>${esc(label)}:</b> ${esc(value)}</p>`;

const exportProfile = (rawForm, scoreInformationList = []) => {
  const form = rawForm || {};
  const profile = form.profile || {};
  const domain = process.env.REACT_APP_DOMAIN;

  loadFile(sample, (error, content) => {
    if (error) throw error;

    const zip = new PizZip(content);
    const doc = new Docxtemplater(zip, {
      linebreaks: true,
      paragraphLoop: true,
      modules: [new HTMLModule({})],
    });

    // Danh sách phân loại sách gộp (quốc tế + trong nước) để tra tên.
    const monographTypeOptions = [
      ...MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL,
      ...MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.DOMESTIC,
    ];

    // ===================== HEADER =====================
    const header = [
      `<p style="text-align:center;font-weight:bold;margin:0;">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</p>`,
      `<p style="text-align:center;font-weight:bold;margin:0;">Độc lập – Tự do – Hạnh phúc</p>`,
      `<p style="text-align:center;margin:0;">------</p>`,
      `<p style="text-align:center;font-weight:bold;margin:14px 0 0;">BẢN ĐĂNG KÝ THAM GIA</p>`,
      `<p style="text-align:center;font-weight:bold;margin:0;">GIẢI THƯỞNG KHOA HỌC CÔNG NGHỆ QUẢ CẦU VÀNG</p>`,
      `<p style="text-align:center;font-weight:bold;margin:0 0 8px;">NĂM 2026</p>`,
    ].join("");

    // ===================== I. THÔNG TIN CÁ NHÂN =====================
    const infoBasic = [
      line("Họ và tên", profile.name),
      line("Nam, nữ", findTitle(FORM_CONSTANTS.GENDER_OPTIONS, profile.gender)),
      line("Ngày sinh", fmtDate(profile.birth)),
      line("Dân tộc", profile.ethnic),
      line(
        "Trình độ học vấn",
        findTitle(FORM_CONSTANTS.ACADEMIC_LEVEL_OPTIONS, form.academicLevel)
      ),
      line("Học hàm/chức danh khoa học", form.academicRank),
      line(
        "Đảng viên/Đoàn viên",
        findTitle(FORM_CONSTANTS.POLITICAL_UNION_OPTIONS, form.politicalUnion) ||
          form.politicalUnion
      ),
      line("Đơn vị giới thiệu", form.nominator),
    ].join("");

    const infoContact = [
      line("Cơ quan đang công tác/học tập", form.unit),
      line("Vị trí/chức vụ công tác", form.position || form.jobTitle || ""),
      line("Địa chỉ", profile.address),
      line("Số điện thoại", profile.phone),
      line("Email", profile.email),
      line("Blog/facebook/website", profile.website),
    ].join("");

    // ===================== II. LĨNH VỰC ĐỀ NGHỊ XÉT THƯỞNG =====================
    const fieldOptions = asArray(FORM_CONSTANTS.FIELD_OPTIONS[CATEGORY.QCV]);
    const fieldList = fieldOptions
      .map(
        (o, i) =>
          `<p style="margin:2px 0;">${i + 1}. ${esc(o.title)}: ${checkbox(
            form.field === o.value
          )}</p>`
      )
      .join("");

    // ===================== III. QUÁ TRÌNH HỌC TẬP, CÔNG TÁC =====================
    const processArr = asArray(parseJson(form.process, []));
    const processRows = processArr.map((p, i) => [
      `${i + 1}`,
      esc(p?.startAt),
      esc(p?.finishAt),
      esc(p?.department),
      esc(p?.works),
    ]);
    const processTable = buildTable(
      [
        "TT",
        "Thời gian bắt đầu",
        "Thời gian kết thúc",
        "Đơn vị công tác",
        "Công việc đảm nhiệm",
      ],
      processRows
    );

    // ===================== IV.A THÀNH TÍCH KHOA HỌC CÔNG NGHỆ =====================
    // A.1 - Bằng độc quyền sáng chế, giải pháp hữu ích
    const solutionRows = asArray(form.formSolution).map((item, i) => {
      const s = item?.solution || {};
      return [
        `${i + 1}`,
        esc(s.title),
        esc(s.author),
        esc(resolveQcvLabel(scoreInformationList, "1", SOLUTIONS_CONSTANTS.TYPE_OPTIONS, s.type)),
        previewCell(domain, s.link || s.preview),
      ];
    });
    const solutionTable = buildTable(
      ["TT", "Tên sản phẩm", "Tác giả", "Phân loại", "Minh chứng"],
      solutionRows
    );

    // A.2 - Bài báo khoa học đăng trên tạp chí quốc tế, quốc gia
    const researchRows = asArray(form.formScientificResearch).map((item, i) => {
      const r = item?.scientificResearch || {};
      const mc = r.link ? previewCell(null, r.link) : previewCell(domain, r.preview);
      return [
        `${i + 1}`,
        esc(r.title),
        esc(r.journalTitle),
        esc(r.publisher),
        esc(r.author),
        esc(findTitle(RESEARCH_CONSTANTS.ROLE_OPTIONS, r.role)),
        mc,
      ];
    });
    const researchTable = buildTable(
      [
        "TT",
        "Tiêu đề bài báo",
        "Tên tạp chí",
        "NXB",
        "Danh sách tác giả",
        "Vai trò",
        "Minh chứng",
      ],
      researchRows
    );

    // A.3 - Bài báo khoa học đăng trên hội thảo quốc tế
    const reportRows = asArray(form.formScientificReport).map((item, i) => {
      const r = item?.scientificReport || {};
      const mc = r.link ? previewCell(null, r.link) : previewCell(domain, r.preview);
      return [
        `${i + 1}`,
        esc(r.title),
        esc(r.conferenceTitle),
        esc(r.publisher),
        esc(r.author),
        esc(findTitle(REPORTS_CONSTANTS.ROLE_OPTIONS, r.role)),
        mc,
      ];
    });
    const reportTable = buildTable(
      [
        "TT",
        "Tiêu đề bài báo",
        "Tên hội thảo",
        "NXB",
        "Danh sách tác giả",
        "Vai trò",
        "Minh chứng",
      ],
      reportRows
    );

    // A.4 - Chương trình, dự án, đề tài nghiên cứu khoa học
    const projectRows = asArray(form.formResearchProject).map((item, i) => {
      const r = item?.researchProject || {};
      return [
        `${i + 1}`,
        esc(r.title),
        esc(resolveQcvLabel(scoreInformationList, "4", RESEARCHPROJECTS_CONSTANTS.LEVEL_OPTIONS, r.level)),
        esc(findTitle(RESEARCHPROJECTS_CONSTANTS.TYPO_OPTIONS, r.type)),
        previewCell(domain, r.link),
      ];
    });
    const projectTable = buildTable(
      ["TT", "Tên đề tài/dự án", "Cấp dự án", "Vai trò", "Minh chứng"],
      projectRows
    );

    // A.5 - Sách chuyên khảo, giáo trình, chương sách
    const monographRows = asArray(form.formMonograph).map((item, i) => {
      const r = item?.monograph || {};
      return [
        `${i + 1}`,
        esc(r.title),
        esc(resolveQcvLabel(scoreInformationList, "5", monographTypeOptions, r.type)),
        esc(findTitle(MONOGRAPHS_CONSTANTS.ROLE_OPTIONS, r.role)),
        previewCell(domain, r.link),
      ];
    });
    const monographTable = buildTable(
      ["TT", "Tiêu đề sách", "Phân loại", "Vai trò", "Minh chứng"],
      monographRows
    );

    // A.6 - Sản phẩm khoa học công nghệ được ứng dụng
    const techRows = asArray(form.formTechCompany).map((item, i) => {
      const t = item?.techCompany || {};
      const valueStr =
        t.value != null && t.value !== ""
          ? `${t.value
              .toString()
              .replace(/\D/g, "")
              .replace(/(?!^)(?=(?:\d{3})+(?:\.|$))/gm, ".")} VNĐ`
          : "";
      const rangeTitle =
        resolveQcvLabel(scoreInformationList, "6", TECH_COMPANY_CONSTANTS.productionOptions, t.applicationRange) ||
        t.applicationRange ||
        t.range ||
        t.scope ||
        "";
      return [
        `${i + 1}`,
        esc(t.production),
        esc(valueStr),
        esc(rangeTitle),
        previewCell(domain, t.link || t.preview),
      ];
    });
    const techTable = buildTable(
      ["TT", "Tên sản phẩm", "Giá trị của sản phẩm", "Phạm vi ứng dụng", "Minh chứng"],
      techRows
    );

    // A.7 - Giải thưởng, huy chương đã đạt được
    const rewardRows = asArray(form.formReward).map((item, i) => {
      const r = item?.reward || {};
      return [
        `${i + 1}`,
        esc(r.title),
        esc(r.author),
        esc(fmtDate(r.decisionDay)),
        previewCell(domain, r.link || r.preview),
      ];
    });
    const rewardTable = buildTable(
      ["TT", "Tên giải thưởng", "Danh sách tác giả", "Ngày quyết định", "Minh chứng"],
      rewardRows
    );

    // A.8 - Công trình tiêu biểu
    const twContent = parseJson(form.typicalWorkContent, null);
    let typicalBody = "";
    if (twContent && typeof twContent === "object" && !Array.isArray(twContent)) {
      typicalBody = QCV2026_TYPICAL_WORK_FIELDS.map(
        (f) =>
          `<p style="margin:4px 0;"><b>${esc(f.label)}:</b> ${esc(
            stripTags(twContent[f.key])
          )}</p>`
      ).join("");
    } else {
      const fallback = stripTags(form.summary);
      typicalBody = fallback
        ? `<p style="margin:4px 0;">${esc(fallback)}</p>`
        : "";
    }
    const typicalSection =
      `<p style="margin:4px 0;"><b>Tên công trình nghiên cứu:</b> ${esc(
        form.typicalWorkName
      )}</p>` + typicalBody;

    // ===================== IV.B THÀNH TÍCH KHEN THƯỞNG =====================
    const commArr = asArray(parseJson(form.communicationActivity, []));
    const rewardLevelOptions = [
      ...QCV2026_REWARD_LEVEL_OPTIONS,
      ...FORM_CONSTANTS.LEVEL_INPUT_OPTIONS,
    ];
    const commRows = commArr.map((r, i) => [
      `${i + 1}`,
      esc(r?.achievementContent),
      esc(findTitle(rewardLevelOptions, r?.typeOfAchievement)),
      previewCell(domain, r?.achievementFile),
    ]);
    const commTable = buildTable(
      ["TT", "Nội dung khen thưởng", "Cấp khen thưởng", "Minh chứng"],
      commRows
    );

    // ===================== IV.C THÀNH TÍCH HOẠT ĐỘNG CỘNG ĐỒNG =====================
    const hrArr = asArray(parseJson(form.hrTrainingActivity, []));
    const hrRows = hrArr.map((r, i) => [
      `${i + 1}`,
      esc(r?.content),
      previewCell(domain, r?.hrTrainingFile),
    ]);
    const hrTable = buildTable(
      ["TT", "Hình thức và nội dung thành tích", "Minh chứng"],
      hrRows
    );

    const socialArr = asArray(parseJson(form.socialActivity, []));
    const socialRows = socialArr.map((r, i) => [
      `${i + 1}`,
      esc(r?.communityAchievement),
      previewCell(domain, r?.communityAchievementFile),
    ]);
    const socialTable = buildTable(
      ["TT", "Hình thức và nội dung thành tích", "Minh chứng"],
      socialRows
    );

    // ===================== IV.D ƯU TIÊN =====================
    const priority = parseJson(form.priority, {});
    const priorityObj =
      priority && typeof priority === "object" && !Array.isArray(priority)
        ? priority
        : {};
    const priorityHtml =
      `<p style="margin:3px 0;">${checkbox(
        !!priorityObj.isEthnicMinority
      )} Là người dân tộc thiểu số</p>` +
      `<p style="margin:3px 0;">${checkbox(
        !!priorityObj.isDisabled
      )} Là người khuyết tật</p>`;

    // ===================== FOOTER =====================
    const footer =
      `<p style="margin:16px 0 4px;">Tôi xin hoàn toàn chịu trách nhiệm trước pháp luật, Ban tổ chức Giải thưởng về tính chính xác và các thông tin đã khai trong hồ sơ đăng ký trực tuyến tham gia xét giải thưởng</p>` +
      `<p style="text-align:right;font-style:italic;margin:8px 0;">…………, ngày……tháng……năm 2026</p>` +
      `<table style="width:100%;border:none;"><tbody><tr>` +
      `<td style="width:50%;text-align:center;border:none;"><b>Xác nhận của Thủ trưởng đơn vị/ Đơn vị đề cử</b><br/><i>(ký tên, đóng dấu)</i></td>` +
      `<td style="width:50%;text-align:center;border:none;"><b>Người khai hồ sơ</b><br/><i>(ký và ghi rõ họ tên)</i><br/><br/><br/><br/>${esc(
        profile.name
      )}</td>` +
      `</tr></tbody></table>`;

    // ===================== GHÉP TOÀN BỘ =====================
    const html = [
      header,

      sectionTitle("I. THÔNG TIN CÁ NHÂN"),
      subTitle("1. Thông tin cơ bản"),
      infoBasic,
      subTitle("2. Thông tin liên lạc"),
      infoContact,

      sectionTitle("II. LĨNH VỰC ĐỀ NGHỊ XÉT THƯỞNG"),
      fieldList,

      sectionTitle("III. QUÁ TRÌNH HỌC TẬP, CÔNG TÁC"),
      processTable,

      sectionTitle("IV. THÀNH TÍCH CÁ NHÂN"),
      subTitle("A. THÀNH TÍCH KHOA HỌC CÔNG NGHỆ"),
      subTitle("1. Bằng độc quyền sáng chế, giải pháp hữu ích"),
      solutionTable,
      subTitle("2. Bài báo khoa học đăng trên tạp chí quốc tế, quốc gia"),
      researchTable,
      subTitle("3. Bài báo khoa học đăng trên hội thảo quốc tế"),
      reportTable,
      subTitle("4. Chương trình, dự án, đề tài nghiên cứu khoa học"),
      projectTable,
      subTitle("5. Sách chuyên khảo, giáo trình, chương sách"),
      monographTable,
      subTitle("6. Sản phẩm khoa học công nghệ được ứng dụng"),
      techTable,
      subTitle("7. Giải thưởng, huy chương đã đạt được"),
      rewardTable,
      subTitle("8. Công trình tiêu biểu"),
      typicalSection,

      subTitle("B. THÀNH TÍCH KHEN THƯỞNG"),
      commTable,

      subTitle("C. THÀNH TÍCH HOẠT ĐỘNG CỘNG ĐỒNG"),
      subTitle(
        "1. Hoạt động bồi dưỡng nhân lực (chia sẻ tri thức, chuyển giao KHCN, tập huấn...) trong lĩnh vực xét giải"
      ),
      hrTable,
      subTitle("2. Hoạt động tình nguyện vì cộng đồng nói chung"),
      socialTable,

      subTitle("D. ƯU TIÊN"),
      priorityHtml,

      footer,
    ].join("");

    doc.render({ content: html });

    const blob = doc.getZip().generate({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    saveAs(blob, "Ban_dang_ky_QCV.docx");
  });
};

export default exportProfile;
